<template>
    <div
        class="tw-inline-block tw-transition-colors tw-duration-150"
        :class="[
            editable ? 'tw-cursor-text' : 'tw-cursor-default',
            editable && bordered
                ? 'tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-1.5 tw-bg-white hover:tw-border-gray-400'
                : editable
                  ? 'tw-border tw-border-transparent tw-rounded-md tw-px-2 tw-py-1 tw-bg-transparent hover:tw-bg-slate-100 focus-within:tw-bg-white focus-within:tw-ring-1 focus-within:tw-ring-slate-300'
                  : 'tw-border tw-border-transparent tw-rounded-md tw-px-0 tw-py-0.5 tw-bg-transparent',
            isEditing && editable ? 'tw-border-[#111827] tw-bg-white tw-ring-1 tw-ring-slate-300' : '',
        ]"
        @click="editable ? focusOnInput() : null"
    >
        <div class="tw-flex tw-items-center" :class="{ 'tw-justify-end': type === 'currency' || type === 'number' }">
            <div v-if="type === 'currency'" class="tw-mr-0.5 tw-text-gray-400">
                {{ currencySymbol ? currencySymbol : localise(0, 0, true).replace('0', '') }}
            </div>
            <div
                v-if="placeholder && !inputValue && !isEditing && editable"
                class="tw-text-gray-400 tw-pl-0.5 tw-pointer-events-none"
                @click="focusOnInput"
            >
                {{ placeholder }}
            </div>
            <div
                ref="inputBox"
                class="tw-min-w-[2px] tw-outline-none"
                :contenteditable="editable"
                @focus="handleFocus"
                @blur="isEditing = false"
                @input="handleInput"
                @keydown="handleKeyPress"
            ></div>
        </div>
    </div>
</template>
<script>
export default {
    props: {
        editable: {
            type: Boolean,
            default: true,
        },
        multiline: {
            type: Boolean,
            default: true,
        },
        modelValue: {
            type: [String, Number],
            default: null,
        },
        placeholder: {
            type: String,
            default: null,
        },
        type: {
            type: String,
            default: null,
        },
        currencySymbol: {
            type: String,
            default: null,
        },
        decimalPlaces: {
            type: Number,
            default: 2,
        },
        maxLength: {
            type: Number,
            default: null,
        },
        minValue: {
            type: Number,
            default: null,
        },
        maxValue: {
            type: Number,
            default: null,
        },
        bordered: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isEditing: false,
            inputValue: null,
        };
    },
    watch: {
        modelValue(newVal) {
            if (newVal != this.inputValue) {
                this.inputValue = typeof newVal === 'number' ? newVal.toString() : newVal;
                this.$refs.inputBox.innerText =
                    (this.type === 'currency' || this.type === 'number') && isNaN(this.inputValue)
                        ? null
                        : this.inputValue;
            }
        },
        inputValue(newVal) {
            if (newVal === '\n') {
                this.inputValue = '';
            }
        },
    },
    mounted() {
        this.inputValue = typeof this.modelValue === 'number' ? this.modelValue.toString() : this.modelValue;
        this.$refs.inputBox.innerText = this.inputValue;
    },
    methods: {
        handleKeyPress(e) {
            if (!this.multiline && e.key === 'Enter') {
                e.preventDefault();
                return;
            }

            if (this.type === 'currency' || this.type === 'number') {
                const allowedInputs = [
                    '0',
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    '.',
                    '-',
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'Tab',
                ];
                const cursorIndex = document.getSelection().anchorOffset;
                if (!allowedInputs.find((c) => c === e.key)) {
                    // Prevent any disallowed inputs from working
                    e.preventDefault(true);
                } else if (e.key === '.' && (this.inputValue ?? '').includes('.')) {
                    // Don't allow multiple decimal points
                    e.preventDefault(true);
                } else if (
                    !isNaN(e.key) &&
                    this.inputValue.includes('.') &&
                    cursorIndex > this.inputValue.indexOf('.') &&
                    this.inputValue.split('.')[1].length > this.decimalPlaces - 1
                ) {
                    // Don't allow more than two digits after a decimal point
                    e.preventDefault(true);
                } else if (e.key === '-' && (this.inputValue.indexOf('-') !== -1 || cursorIndex !== 0)) {
                    // Don't allow a minus if either one already exists or caret is not at the start of the figure
                    e.preventDefault(true);
                } else if (cursorIndex === 0 && this.inputValue.startsWith('-')) {
                    // Don't allow figures to be input before a minus symbol
                    e.preventDefault(true);
                } else if (e.key === '.' && !this.decimalPlaces) {
                    // Don't allow decimal points if places is set to 0
                    e.preventDefault(true);
                } else if (e.key === '0' && cursorIndex === 0 && this.inputValue && this.inputValue.length > 0) {
                    const currentSelection = document.getSelection();
                    if (currentSelection && !currentSelection.isCollapsed) {
                        return;
                    }
                    // Don't allow a 0 to be put before existing figures
                    e.preventDefault(true);
                } else if (e.key === 'Tab') {
                    this.handlePostTabPress();
                }
            }
            if (
                this.maxLength &&
                this.inputValue &&
                this.inputValue.length >= this.maxLength &&
                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)
            ) {
                // Don't allow input greather than the max length if specified
                e.preventDefault(true);
            } else if (e.key === 'Tab') {
                this.handlePostTabPress();
            }
        },
        handleInput(e) {
            let newVal = e.target.innerText;
            let shouldRepositionCursor = false;

            if (!this.multiline && /[\r\n]/.test(newVal)) {
                newVal = newVal.replace(/[\r\n]/g, '');
                this.$refs.inputBox.innerText = newVal;
                shouldRepositionCursor = true;
            }

            if (!isNaN(newVal) && (this.type === 'currency' || this.type === 'number')) {
                if (this.maxValue && !isNaN(this.maxValue) && parseFloat(newVal) >= this.maxValue) {
                    // Limit to max value if value exceeds
                    newVal = this.maxValue.toString();
                    this.$refs.inputBox.innerText = newVal;
                    shouldRepositionCursor = true;
                } else if (this.minValue && !isNaN(this.minValue) && parseFloat(newVal) <= this.minValue) {
                    // Limit to min value if value goes below
                    newVal = this.minValue.toString();
                    this.$refs.inputBox.innerText = newVal;
                    shouldRepositionCursor = true;
                } else if (newVal.startsWith('0') && newVal.length > 1 && !newVal.startsWith('0.')) {
                    // Replace any leading zeros (eg. replacing 0402.22 with 402.22)
                    newVal = newVal.replace('0', '');
                    this.$refs.inputBox.innerText = newVal;
                    shouldRepositionCursor = true;
                }
            }

            if (shouldRepositionCursor) {
                this.$nextTick().then(() => {
                    this.moveCursorToEnd();
                });
            }

            if (newVal === this.inputValue) {
                return;
            }

            this.inputValue = newVal;
            this.$emit(
                'update:modelValue',
                ['currency', 'number'].includes(this.type)
                    ? isNaN(this.inputValue)
                        ? 0
                        : parseFloat(this.inputValue ? this.inputValue : 0)
                    : this.inputValue
            );
        },
        handlePostTabPress() {
            setTimeout(() => {
                this.moveCursorToEnd();
            }, 10);
        },
        handleFocus() {
            this.isEditing = true;
            this.$nextTick().then(() => {
                this.moveCursorToEnd();
            });
        },
        moveCursorToEnd() {
            const inputBox = this.$refs.inputBox;
            if (!inputBox || document.activeElement !== inputBox) {
                return;
            }

            const currentSelection = document.getSelection();
            const range = document.createRange();

            // If there's content, move cursor to the end
            if (inputBox.childNodes.length > 0) {
                const lastNode = inputBox.childNodes[inputBox.childNodes.length - 1];
                const textLength = lastNode.textContent?.length || 0;
                range.setStart(lastNode, textLength);
                range.collapse(true);
            } else {
                // If empty, just set range on the element
                range.selectNodeContents(inputBox);
                range.collapse(false);
            }

            currentSelection.removeAllRanges();
            currentSelection.addRange(range);
        },
        focusOnInput() {
            this.isEditing = true;
            this.$refs.inputBox.focus();
        },
    },
};
</script>
<style lang="scss" scoped>
.editable-text {
    display: inline-block;
    padding: 2px 0px;
    border: 1px solid transparent;
    border-radius: 3px;
    transition: all 0.1s ease;
    background: white;

    &:not(.editable) {
        cursor: default;
    }
    &.editable {
        padding: 2px 4px;

        &.bordered {
            border: 1px solid rgba(black, 0.1);
        }

        &:hover,
        &:focus-within {
            border: 1px solid rgba(black, 0.33);
            transform: scale(1.05);
        }

        &.editing {
            border: 1px solid rgba(black, 0.25);
            background: white;
        }
    }

    & > .editable-text-wrapper {
        display: flex;

        & > .editable-text-input {
            min-width: 2px;
        }

        & > .editable-text-placeholder {
            opacity: 0.5;
            padding-left: 2px;
        }
    }
}
</style>
